"use client";

import React, { useState } from "react";

import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormField,
  FormItem,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";

import { Loader2 } from "lucide-react";

import { tinaFieldSafe } from "@/utils";

import animationData from "@/assets/success.json";

import dynamic from "next/dynamic";

const Lottie = dynamic(() => import("react-lottie-player"), { ssr: false });

const formSchema = z.object({
  firstName: z.string().min(1, "Required"),
  lastName: z.string().min(1, "Required"),
  companyName: z.string().optional(),
  companyEmail: z.string().email({
    message: "Invalid email",
  }),
});

export default function CommunityEditionForm({
  formSection,
}: {
  formSection: any;
}) {
  const [isSuccess, setIsSuccess] = useState(false);

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      companyName: "",
      companyEmail: "",
    },
  });

  async function onSubmit(values: any) {
    try {
      const response = await fetch("/api/hubspot/submit-community-edition", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          firstName: values.firstName,
          lastName: values.lastName,
          companyName: values.companyName,
          companyEmail: values.companyEmail,
          formGuid: formSection?.formGuid,
          portalId: formSection?.portalId,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to submit form");
      }

      form.reset();
      setIsSuccess(true);
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Something went wrong. Please try again.";
      
      // Include fallback URL in error if available
      const fullErrorMessage = formSection?.fallbackFormUrl
        ? `Unexpected error. You can also try submitting directly: ${formSection.fallbackFormUrl}`
        : errorMessage;
      
      form.setError("root", {
        type: "manual",
        message: fullErrorMessage,
      });
    }
  }

  return (
    <div className="relative">
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="grid grid-cols-1 md:grid-cols-2 gap-y-6 gap-x-4 text-foreground-100 [&_label]:mb-1"
        >
          <FormField
            control={form.control}
            name="firstName"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input placeholder="First name*" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="lastName"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input placeholder="Last name*" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="companyName"
            render={({ field }) => (
              <FormItem className="md:col-span-2">
                <FormControl>
                  <Input placeholder="Company name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="companyEmail"
            render={({ field }) => (
              <FormItem className="md:col-span-2">
                <FormControl>
                  <Input placeholder="Company email*" {...field} type="email" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="md:col-span-2 w-full">
            <Button
              type="submit"
              className="w-full disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={form.formState.isSubmitting}
            >
              {form.formState.isSubmitting ? (
                <Loader2 className="animate-spin size-5" />
              ) : (
                "Get Started"
              )}
            </Button>

            <div className="mt-4">
              {form.formState.errors.root && (
                <div className="flex flex-wrap text-center items-center justify-center gap-2">
                  <div className="text-red-500 text-sm font-medium text-center">
                    {form.formState.errors.root.message?.includes("http") ? (
                      <>
                        {form.formState.errors.root.message.split(
                          formSection?.fallbackFormUrl
                        )[0]}
                        <a
                          href={formSection?.fallbackFormUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="underline hover:no-underline"
                        >
                          {formSection?.fallbackFormUrl}
                        </a>
                      </>
                    ) : (
                      form.formState.errors.root.message
                    )}
                  </div>
                </div>
              )}
            </div>

            {formSection?.disclaimer && (
              <div className="mt-4">
                <p
                  className="!text-xs text-muted-foreground"
                  data-tina-field={tinaFieldSafe(formSection, "disclaimer")}
                >
                  {formSection.disclaimer}
                </p>
              </div>
            )}
          </div>
          {isSuccess && (
            <SuccessMessage
              title={formSection?.successMessage}
              subtitle={formSection?.successMessageSubtitle}
              tinaData={formSection}
            />
          )}
        </form>
      </Form>
    </div>
  );
}

export const SuccessMessage = ({ title, subtitle, tinaData }) => {
  return (
    <div className="absolute top-0 left-0 w-[calc(100%+4px)] -ml-0.5 h-full flex flex-col items-center justify-center bg-background rounded-lg text-center px-3">
      <div className="w-44 min-h-44">
        <Lottie
          loop={false}
          play
          animationData={animationData}
          className="success-animation w-48 h-48 [&_svg_path]:fill-secondary-700"
        />
      </div>
      <div className="flex flex-col gap-1">
        <div
          className="h4 mb-1 text-balance"
          data-tina-field={tinaFieldSafe(tinaData, "successMessage")}
        >
          {title}
        </div>

        <div
          className="p-lg text-foreground-500 text-center max-w-[400px]"
          data-tina-field={tinaFieldSafe(tinaData, "successMessageSubtitle")}
        >
          {subtitle}
        </div>
      </div>
    </div>
  );
};
